import axios from 'axios';

import type {
  AddonAttributes,
  BillingContact,
  BillingMode,
  CMSPlanSchema,
  CMSPlanType,
  Licence,
  ManagedSubscription,
  ManualLicenceProps,
  PersistedStripePrice,
  ProductUsage,
  SanitizedBillingSite,
  SanitizedCustomer,
  SanitizedInvoice,
  SanitizedSubscription,
  SanitizedSubscriptionProRata,
  SelfServeSubscription,
  TrialReport,
  WorkflowTrialData,
  WorkflowsTrialSettings,
} from '@sine/sine-core-api-types';

import { type AddonType } from '../../team/billingV2/constants/billing.constants';
import type { Card, Currency } from './billing.types';

type CreateManagedSubscriptionParams = {
  licences: Array<{
    plan: string;
    site?: string | null;
  }>;
  team: string;
  currency?: Currency;
};

type CreateManualLicenseParams = {
  licences: Array<ManualLicenceProps>;
  subscriptionId: string;
  teamId: string;
};

type CreateManualSubscriptionParams = {
  convertToStripe: boolean;
  startDate: Date | string;
  endDate: Date | string;
  licences: Array<ManualLicenceProps>;
  teamId: string;
  cmsPlan?: `${CMSPlanType}`;
};

const BILLING_ENDPOINT = `${window.env.REACT_APP_SINE_CORE_API_URI}/billing`;

const billingService = {
  async listSubscriptions(teamId: string) {
    const { data } = await axios.get<Array<SanitizedSubscription>>(`${BILLING_ENDPOINT}/team/${teamId}/subscriptions`);
    return data;
  },

  async listPaymentHistories(teamId: string, subscriptionId: string) {
    const { data } = await axios.get<Array<SanitizedInvoice>>(
      `${BILLING_ENDPOINT}/team/${teamId}/subscription/${subscriptionId}/payments`,
    );
    return data;
  },

  // null means that the customer is not yet created on Stripe
  async getCustomer(teamId: string) {
    const { data } = await axios.get<SanitizedCustomer | undefined | null>(
      `${BILLING_ENDPOINT}/team/${teamId}/customer`,
    );
    return data;
  },

  async listPrices(teamId: string, customerCurrency: Currency, oldAndNewPrices = false) {
    const { data } = await axios.get<Array<PersistedStripePrice>>(
      `${BILLING_ENDPOINT}/team/${teamId}/prices?currency=${customerCurrency}&all=${oldAndNewPrices}`,
    );
    return data;
  },

  async listSites(teamId: string) {
    const { data } = await axios.get<Array<SanitizedBillingSite>>(`${BILLING_ENDPOINT}/team/${teamId}/sites`);
    return data;
  },

  async createSubscription(params: CreateManagedSubscriptionParams) {
    const { data } = await axios.post<SelfServeSubscription>(`${BILLING_ENDPOINT}/subscription`, params);
    return data;
  },

  async createManualLicence(params: CreateManualLicenseParams) {
    const { data } = await axios.post<ManagedSubscription>(`${BILLING_ENDPOINT}/licence/manual`, params);
    return data;
  },

  async createManualSubscription(params: CreateManualSubscriptionParams) {
    const { data } = await axios.post<ManagedSubscription>(`${BILLING_ENDPOINT}/subscription/manual`, params);
    return data;
  },

  async cancelSubscription(teamId: string, subscriptionId: string) {
    const { data } = await axios.post<void>(`${BILLING_ENDPOINT}/subscription/cancel`, {
      team: teamId,
      subscription: subscriptionId,
    });
    return data;
  },

  async cancelLicence(teamId: string, licenceId: string) {
    const { data } = await axios.post<void>(`${BILLING_ENDPOINT}/licence/cancel`, {
      team: teamId,
      licence: licenceId,
    });
    return data;
  },

  async assignLicence(teamId: string, licenceId: string, siteId: string) {
    const { data } = await axios.post<Licence>(`${BILLING_ENDPOINT}/assign-licence-to-site`, {
      team: teamId,
      licence: licenceId,
      site: siteId,
    });
    return data;
  },

  async createLicenceAddon(params: { teamId: string; licenceId: string; addonType: `${AddonType}` }) {
    const { data } = await axios.post<AddonAttributes>(`${BILLING_ENDPOINT}/licence/create-licence-addon`, params);
    return data;
  },

  async cancelLicenceAddon(params: { teamId: string; licenceId: string; addon: `${AddonType}` }) {
    const { data } = await axios.post<AddonAttributes>(`${BILLING_ENDPOINT}/licence/cancel-licence-addon`, params);
    return data;
  },

  async deleteLicence(licenceId: string) {
    const { data } = await axios.delete<void>(`${BILLING_ENDPOINT}/licence/manual/${licenceId}`);
    return data;
  },

  async releaseLicence(teamId: string, licenceId: string) {
    const { data } = await axios.post<Licence>(`${BILLING_ENDPOINT}/release-licence`, {
      team: teamId,
      licence: licenceId,
    });
    return data;
  },

  async getUpcomingInvoice(teamId: string, subscriptionId: string) {
    const { data } = await axios.get<{
      externalData: {
        next_payment_attempt: number | null;
        amount_due: number;
      };
    }>(`${BILLING_ENDPOINT}/team/${teamId}/subscription/${subscriptionId}/upcoming-invoice`);
    return data;
  },

  async updateCards(teamId: string, cards: Array<Pick<Card, 'id' | 'default'>>) {
    const { data } = await axios.post<void>(`${BILLING_ENDPOINT}/update-cards`, { team: teamId, cards });
    return data;
  },

  async updateLicenceNote(licenceId: string, licenceNote: string) {
    const { data } = await axios.put<Licence>(`${BILLING_ENDPOINT}/licence/${licenceId}/note`, {
      licenceId,
      licenceNote,
    });
    return data;
  },

  async getTrialData() {
    const { data } = await axios.get<TrialReport>(`${BILLING_ENDPOINT}/trial-data`);
    return data;
  },

  async updateBillingMode(teamId: string, params: Partial<Omit<BillingMode, 'team'>>) {
    const { data } = await axios.put<Pick<BillingMode, 'mode' | 'team'>>(
      `${BILLING_ENDPOINT}/team/${teamId}/mode`,
      params,
    );
    return data;
  },

  async getBillingMode(teamId: string) {
    const { data } = await axios.get<Pick<BillingMode, 'mode' | 'team'>>(`${BILLING_ENDPOINT}/team/${teamId}/mode`);
    return data.mode;
  },

  async getBillingContact(teamId: string) {
    const { data } = await axios.get<BillingContact | undefined>(`${BILLING_ENDPOINT}/team/${teamId}/contact`);
    return data;
  },

  async getBillingUsage(teamId: string) {
    const { data } = await axios.get<ProductUsage>(`${BILLING_ENDPOINT}/team/${teamId}/product-usage`);
    return data;
  },

  async getWorkflowsTrialData() {
    const { data } = await axios.get<WorkflowTrialData>(`${BILLING_ENDPOINT}/workflows/trial`);
    return data;
  },

  async cancelWorkflowTrial() {
    const { data } = await axios.post(`${BILLING_ENDPOINT}/workflows/trial/cancel`);
    return data;
  },

  async extendWorkflowTrial() {
    const { data } = await axios.post(`${BILLING_ENDPOINT}/workflows/trial/extend`);
    return data;
  },

  async manageWorkflowTrialSettings(settings: WorkflowsTrialSettings) {
    const { data } = await axios.put(`${BILLING_ENDPOINT}/workflows/trial/settings`, { settings });
    return data;
  },

  async getCMSPlan() {
    const { data } = await axios.get<CMSPlanSchema>(`${BILLING_ENDPOINT}/cms/plan`);
    return data;
  },

  async createOrUpdateCMSPlan(planType: string, cutOffDate: Date) {
    const { data } = await axios.put<WorkflowTrialData>(`${BILLING_ENDPOINT}/cms/plan`, {
      planType,
      cutOffDate,
    });
    return data;
  },

  async proRateSubscription(
    subscriptionId: string,
    items: Array<{ id?: string; newPriceId: string; siteId?: string }>,
  ) {
    const { data } = await axios.post<SanitizedSubscriptionProRata>(`${BILLING_ENDPOINT}/subscription/pro-rate`, {
      subscriptionId,
      items,
    });
    return data;
  },

  async proRateSubscriptions(
    subscriptions: Array<{ subscriptionId: string; items: Array<{ id: string; newPriceId: string; siteId?: string }> }>,
  ) {
    const { data } = await axios.post<Array<SanitizedSubscriptionProRata>>(
      `${BILLING_ENDPOINT}/subscriptions/pro-rate`,
      {
        subscriptions,
      },
    );
    return data;
  },

  async updateSubscription(subscriptionId: string, items: Array<{ id: string; newPriceId: string; siteId?: string }>) {
    const { data } = await axios.post<SanitizedSubscription>(`${BILLING_ENDPOINT}/subscription/update`, {
      subscriptionId,
      items,
    });
    return data;
  },

  async updateSubscriptions(
    subscriptions: Array<{ subscriptionId: string; items: Array<{ id: string; newPriceId: string; siteId?: string }> }>,
  ) {
    const { data } = await axios.post<SanitizedSubscription>(`${BILLING_ENDPOINT}/subscriptions/update`, {
      subscriptions,
    });
    return data;
  },

  async updateManualSubscription(
    subscriptionId: string,
    startDate: string,
    endDate: string,
    items: Array<{ id: string | null; type: string; note: string }>,
  ) {
    const { data } = await axios.post<SanitizedSubscription>(
      `${BILLING_ENDPOINT}/subscription/${subscriptionId}/manual/update`,
      {
        startDate,
        endDate,
        items,
      },
    );
    return data;
  },
};

export default billingService;
